import { useState, useMemo } from "react";
import {
  GameLogs,
  getColorForGamesPlayed,
  getWeekOfYear,
  formatDateForTooltip,
  getMonthLabels,
} from "./helpers/year-in-review-playtime-helpers";

interface TooltipData {
  x: number;
  y: number;
  date: string;
  gamesPlayed: number;
  wins: number;
  losses: number;
}

interface PlayTimeChartProps {
  activityData: GameLogs[];
}

// Constants for layout
const CELL_SIZE = 13;
const CELL_GAP = 4;
const CELL_TOTAL = CELL_SIZE + CELL_GAP;
const DAY_LABEL_WIDTH = 30;
const MONTH_LABEL_HEIGHT = 20;
const DAY_LABEL_SPACING = 12;
const MONTH_LABEL_SPACING = 12;
const GRID_START_X = DAY_LABEL_WIDTH + DAY_LABEL_SPACING;
const GRID_START_Y = MONTH_LABEL_HEIGHT + MONTH_LABEL_SPACING;

const dayLabels = [
  { day: "Mon", index: 1 },
  { day: "Wed", index: 3 },
  { day: "Fri", index: 5 },
];

export const PlayTimeChart = ({ activityData }: PlayTimeChartProps) => {
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const monthLabels = useMemo(() => getMonthLabels(2025), []);
  const cells: JSX.Element[] = [];

  const activityMap = useMemo(() => {
    const map = new Map<string, GameLogs>();
    activityData.forEach((day) => {
      map.set(day.date, day);
    });
    return map;
  }, [activityData]);

  const startDate = new Date(2025, 0, 1);
  const endDate = new Date(2025, 11, 31);
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const dateString = currentDate.toISOString().split("T")[0];
    const dayOfWeek = currentDate.getDay();
    const weekOffset = getWeekOfYear(currentDate);
    const dayData = activityMap.get(dateString) || {
      date: dateString,
      gamesPlayed: 0,
      wins: 0,
      losses: 0,
    };

    const x = GRID_START_X + weekOffset * CELL_TOTAL;
    const y = GRID_START_Y + dayOfWeek * CELL_TOTAL;

    const color = getColorForGamesPlayed(dayData.gamesPlayed);

    cells.push(
      <rect
        key={dateString}
        x={x}
        y={y}
        width={CELL_SIZE}
        height={CELL_SIZE}
        rx={2}
        fill={color}
        className="transition-all duration-150 cursor-pointer hover:brightness-125 hover:stroke-[#CDDCFE] hover:stroke-[1px]"
        onMouseEnter={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setTooltip({
            x: rect.left + rect.width / 2,
            y: rect.top - 10,
            date: dayData.date,
            gamesPlayed: dayData.gamesPlayed,
            wins: dayData.wins,
            losses: dayData.losses,
          });
        }}
        onMouseLeave={() => setTooltip(null)}
      />,
    );
    currentDate.setDate(currentDate.getDate() + 1);
    currentDate.setHours(0, 0, 0, 0);
  }

  return (
    <div className="relative flex shrink-0 items-center justify-center rounded-[3px] bg-[#070720] border-[1px] border-[#191937] w-[978px] h-[214px] px-[20px] py-[16px]">
      <svg width="939" height="180" className="overflow-visible">
        {monthLabels.map((label) => (
          <text
            key={label.month}
            x={GRID_START_X + label.weekOffset * CELL_TOTAL}
            y={12}
            className="text-[14px] fill-[#CDDCFE] font-normal"
          >
            {label.month}
          </text>
        ))}
        {dayLabels.map((label) => (
          <text
            key={label.day}
            x={0}
            y={GRID_START_Y + label.index * CELL_TOTAL + CELL_SIZE / 2 + 3}
            className="text-[14px] fill-[#CDDCFE] font-normal pr-[12px]"
          >
            {label.day}
          </text>
        ))}
        {cells}
      </svg>
      <div className="absolute bottom-4 right-5 flex items-center gap-2">
        <span className="text-[10px] text-[#5C5C8E]">Less</span>
        <div className="flex gap-1">
          {[0, 1, 2, 3, 4].map((level) => (
            <div
              key={level}
              className="w-[11px] h-[11px] rounded-[2px]"
              style={{
                backgroundColor: getColorForGamesPlayed(
                  level === 0 ? 0 : level === 1 ? 1 : level === 2 ? 2 : level === 3 ? 3 : level === 4 ? 5 : 6,
                ),
              }}
            />
          ))}
        </div>
        <span className="text-[10px] text-[#5C5C8E]">More</span>
      </div>

      {tooltip && (
        <div
          className="fixed z-50 pointer-events-none shadow-[0_1px_0_0_#383864_inset] drop-shadow-[0_2px_2px_rgba(0,0,0,0.40)] rounded border-t border-t-[#D9D9D9]"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: "translate(-50%, -100%)",
          }}
        >
          <svg
            className="absolute w-[18px] h-[18px] bg-[#191937] -bottom-[9px] left-1/2 -translate-x-1/2"
            style={{
              transform: "translateX(-50%) rotate(45deg)",
            }}
          />
          <div className="flex flex-col items-center gap-y-[4px] px-[10px] py-[12px] rounded bg-[#191937] relative">
            <div className="text-[12px] text-white font-medium whitespace-nowrap">
              {tooltip.gamesPlayed} {tooltip.gamesPlayed === 1 ? "game" : "games"} ({tooltip.wins}W {tooltip.losses}L)
            </div>
            <div className="text-[10px] text-[#CDDCFE]"> {formatDateForTooltip(tooltip.date)} </div>
          </div>
        </div>
      )}
    </div>
  );
};
